import { useEffect, useRef } from 'react'

interface Star {
  x: number
  y: number
  size: number
  brightness: number
  twinkleSpeed: number
  twinklePhase: number
}

export function AnimatedStarField() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const starsRef = useRef<Star[]>([])
  const animationRef = useRef<number>()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Initialize stars
    const initStars = () => {
      starsRef.current = []
      const numStars = 150 // More stars for better coverage
      
      for (let i = 0; i < numStars; i++) {
        starsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 1.5 + 0.5,
          brightness: Math.random() * 0.6 + 0.2,
          twinkleSpeed: 0.02 + Math.random() * 0.03, // Different twinkle speeds
          twinklePhase: Math.random() * Math.PI * 2 // Random starting phase
        })
      }
    }

    // Resize canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      initStars()
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Animation loop
    const animate = () => {
      if (!ctx || !canvas) return

      // Clear canvas with deep space black
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Add subtle gradient for depth
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height) / 2
      )
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0)')
      gradient.addColorStop(1, 'rgba(0, 0, 20, 0.2)')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Update and draw twinkling stars
      starsRef.current.forEach(star => {
        // Update twinkle phase
        star.twinklePhase += star.twinkleSpeed
        
        // Calculate twinkling brightness using sine wave
        const twinkle = (Math.sin(star.twinklePhase) + 1) / 2 // 0 to 1
        const alpha = star.brightness * (0.3 + twinkle * 0.7) // Varies between 30% and 100% of base brightness
        
        // Draw twinkling star
        ctx.save()
        
        // Subtle glow effect
        ctx.shadowBlur = star.size * 2
        ctx.shadowColor = '#ffffff'
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`
        ctx.beginPath()
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2)
        ctx.fill()
        
        // Bright core
        ctx.shadowBlur = 0
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 1.5})`
        ctx.beginPath()
        ctx.arc(star.x, star.y, star.size * 0.3, 0, Math.PI * 2)
        ctx.fill()
        
        // Occasional bright twinkle
        if (twinkle > 0.9) {
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 2})`
          ctx.beginPath()
          ctx.arc(star.x, star.y, star.size * 0.1, 0, Math.PI * 2)
          ctx.fill()
        }
        
        ctx.restore()
      })

      animationRef.current = requestAnimationFrame(animate)
    }

    // Start animation
    animate()

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
      style={{ background: 'transparent' }}
    />
  )
}